import React from 'react';
import { DateTime } from 'luxon';
import { useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { Badge } from './ui/badge';
import { Calendar, Users } from 'lucide-react';
import { getAccessibleTextColor } from '../lib/color';
import { useTheme } from '../hooks/useTheme';

const DailySummary = ({ 
  events, 
  hasMultipleCalendars, 
  hoveredEventNumber, 
  setHoveredEventNumber,
  clickedEventNumber,
  onClose,
  selectedDate,
  use24HourFormat
}) => {
  const { user } = useUser();
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();

  const getContrastTextColor = (backgroundColor) => getAccessibleTextColor(backgroundColor);

  if (!events || events.length === 0) {
    return null;
  }

  const formatParticipants = (attendees) => {
    if (!attendees || attendees.length === 0) {
      return t('dailySummary.noParticipants');
    }

    const participants = attendees
      .filter(attendee => attendee.displayName || attendee.email)
      .map(attendee => attendee.displayName || attendee.email);

    if (participants.length === 0) {
      return t('dailySummary.noParticipants');
    }

    if (participants.length <= 5) {
      return participants.join(', ');
    }

    return participants.slice(0, 5).join(', ') + '...';
  };

  const formatTime = (startTime, endTime, allDay) => {
    if (allDay) {
      return t('dailySummary.allDay');
    }

    const lang = i18n.language || 'en';
    const start = DateTime.fromISO(startTime).setZone('America/New_York').setLocale(lang);
    const end = DateTime.fromISO(endTime).setZone('America/New_York').setLocale(lang);

    const timeFormat = use24HourFormat ? DateTime.TIME_24_SIMPLE : DateTime.TIME_SIMPLE;
    return `${start.toLocaleString(timeFormat)} - ${end.toLocaleString(timeFormat)}`;
  };

  const formatDescription = (description) => {
    if (!description || description.trim() === '') {
      return t('dailySummary.noDescription');
    }

    // Remove HTML tags and clean up the description
    const cleanDescription = description
      .replace(/<[^>]*>/g, '')  // Remove HTML tags
      .replace(/&[^;]+;/g, ' ') // Remove HTML entities
      .trim();

    // Truncate if too long
    if (cleanDescription.length > 200) {
      return cleanDescription.substring(0, 200) + '...';
    }

    return cleanDescription;
  };

  // Format the date for the title. English keeps the "May 7th, 2026" form
  // with an ordinal suffix; other locales use Luxon's localized full date
  // (e.g. "7 mai 2026", "7. Mai 2026", "2026年5月7日").
  const formatDateForTitle = (date) => {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? DateTime.fromISO(date) : date;
    const lang = i18n.language || 'en';
    const localized = dateObj.setLocale(lang);

    if (lang.startsWith('en')) {
      const day = dateObj.day;
      const suffix = getOrdinalSuffix(day);
      return `${localized.toFormat('MMMM')} ${day}${suffix}, ${localized.toFormat('yyyy')}`;
    }

    return localized.toLocaleString(DateTime.DATE_FULL);
  };

  // English ordinal suffix (1st, 2nd, 3rd, …). Other locales don't use this.
  const getOrdinalSuffix = (day) => {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  return (
    <div className="daily-summary">
      <div className="daily-summary-header">
        <h2 className="daily-summary-title">{t('dailySummary.title', { date: formatDateForTitle(selectedDate) })}</h2>
        <button 
          className="daily-summary-close"
          onClick={onClose}
          aria-label={t('dailySummary.closeLabel')}
        >
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div className="daily-summary-events">
        {events.map((event, index) => {
          // Apply same confirmation logic as small circles
          const userEmails = [
            user?.primaryEmailAddress?.emailAddress,
            ...(user?.emailAddresses?.map(e => e.emailAddress) || [])
          ].filter(Boolean);
          
          const userAttendee = event.attendees?.find(
            attendee => userEmails.includes(attendee.email)
          );
          
          // If no direct email match, look for self=true attendees (user is authenticated to this calendar)
          const selfAttendee = event.attendees?.find(
            attendee => attendee.self === true
          );
          
          // Use self attendee if no direct email match found
          const finalUserAttendee = userAttendee || selfAttendee;
          const responseStatus = finalUserAttendee?.responseStatus || 'needsAction';
          
          // Check if user has self=true in attendees (organizer/creator)
          const userSelfAttendee = event.attendees?.find(
            attendee => userEmails.includes(attendee.email) && attendee.self === true
          ) || selfAttendee; // Fallback to any self=true attendee
          
          // Check if user is the only participant
          const isOnlyParticipant = event.attendees && event.attendees.length === 1 && 
                                  (userEmails.some(email => 
                                    event.attendees?.some(attendee => attendee.email === email)
                                  ) || finalUserAttendee); // Include self attendees
          
          // Determine if event is confirmed
          // responseStatus=accepted should always be filled/confirmed
          const isConfirmed = (responseStatus === 'accepted') ||
                            event.allDay || 
                            (event.status === 'confirmed' && (!event.attendees || event.attendees.length === 0)) ||
                            (event.status === 'confirmed' && isOnlyParticipant && responseStatus === 'needsAction' && !userSelfAttendee) ||
                            (event.status === 'confirmed' && 
                             responseStatus !== 'needsAction' && 
                             responseStatus !== 'declined' && 
                             (userEmails.some(email => 
                               event.attendees?.some(attendee => attendee.email === email)
                             ) || finalUserAttendee)); // Include self attendees

          return (
          <div
            key={`${event.eventId || event.id}-${event.sequentialNumber || index}`}
            id={`event-${event.sequentialNumber}`}
            className={`event-card ${event.sequentialNumber && hoveredEventNumber === event.sequentialNumber ? 'highlighted' : ''} ${event.sequentialNumber && clickedEventNumber === event.sequentialNumber ? 'clicked' : ''}`}
            onMouseEnter={() => event.sequentialNumber && setHoveredEventNumber(event.sequentialNumber)}
            onMouseLeave={() => event.sequentialNumber && setHoveredEventNumber(null)}
          >
            <div className="event-header">
              <div className="event-number-title">
                {event.sequentialNumber && (
                  <span 
                    className="event-number"
                    style={{
                      backgroundColor: isConfirmed ? 'rgba(0,0,0,0.8)' : 'white',
                      color: isConfirmed ? 'white' : 'rgba(0,0,0,0.8)',
                      border: isConfirmed 
                        ? (isDarkMode ? '2px solid white' : 'none')
                        : '2px solid rgba(0,0,0,0.8)'
                    }}
                  >
                    {event.sequentialNumber}
                  </span>
                )}
                <h3 className="event-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>{event.title}</span>
                  {event.htmlLink && (
                    <button 
                      className="event-link-btn option-icon-btn" 
                      onClick={() => window.open(event.htmlLink, '_blank', 'noopener,noreferrer')}
                      aria-label={t('events.viewEventLabel')}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        position: 'relative'
                      }}
                    >
                      <Calendar size={15} />
                    </button>
                  )}
                </h3>
              </div>
              <div className="event-actions">
                {hasMultipleCalendars && (
                  <Badge 
                    variant="secondary"
                    style={{ 
                      backgroundColor: event.calendarColor, 
                      color: getContrastTextColor(event.calendarColor),
                      border: 'none'
                    }}
                  >
                    {event.calendarName}
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="event-time">
              {formatTime(event.startTime, event.endTime, event.allDay)}
            </div>
            
            <div className="event-description">
              {formatDescription(event.description)}
            </div>
            
            <div className="event-participants">
              <Users size={14} className="inline mr-2" />
              {formatParticipants(event.attendees)}
            </div>
          </div>
        );
        })}
      </div>
    </div>
  );
};

export default DailySummary;